import React from 'react';
import { FiArrowUpRight, FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import IconWrapper from '../IconWrapper';

interface NCDScoreProps {
  title: string;
  score: number;
  maxScore?: number;
  previousScore?: number;
  description?: string;
  details?: string;
  trend?: 'up' | 'down' | 'stable';
}

const NCDScoreCard: React.FC<NCDScoreProps> = ({ 
  title, 
  score, 
  maxScore = 100, 
  previousScore,
  description,
  details,
  trend 
}) => {
  // Calculate percentage
  const percentage = (score / maxScore) * 100;
  
  // Determine score level and color
  let scoreLevel = '';
  let scoreColor = '';
  
  if (percentage >= 90) {
    scoreLevel = 'Excellent';
    scoreColor = 'bg-green-500';
  } else if (percentage >= 75) {
    scoreLevel = 'Good';
    scoreColor = 'bg-green-400';
  } else if (percentage >= 60) {
    scoreLevel = 'Satisfactory';
    scoreColor = 'bg-yellow-400';
  } else if (percentage >= 40) {
    scoreLevel = 'Needs Improvement';
    scoreColor = 'bg-orange-400';
  } else {
    scoreLevel = 'Critical';
    scoreColor = 'bg-red-500';
  }
  
  // Calculate trend percentage if previous score exists
  const trendPercentage = previousScore ? ((score - previousScore) / previousScore) * 100 : 0;
  
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-bold text-secondary">{title}</h3>
        <button className="text-primary text-sm font-medium flex items-center hover:underline">
          View Details
          <IconWrapper icon={FiArrowUpRight} className="ml-1" size={16} />
        </button>
      </div>
      
      {description && (
        <p className="text-sm text-secondary/70 mb-6">{description}</p>
      )}
      
      <div className="flex items-end gap-6 mb-6">
        <div className="relative w-32 h-32">
          <svg viewBox="0 0 36 36" className="w-full h-full">
            {/* Background circle */}
            <path
              d="M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              stroke="#E6E6E6"
              strokeWidth="2"
              strokeDasharray="100, 100"
            />
            {/* Foreground circle */}
            <path
              d="M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              stroke={scoreColor.replace('bg-', 'var(--color-')}
              strokeWidth="3"
              strokeDasharray={`${percentage}, 100`}
              className="transform -rotate-90 origin-center"
            />
            {/* Text in the center */}
            <text 
              x="18" 
              y="18" 
              textAnchor="middle" 
              dominantBaseline="middle" 
              fontSize={`${10 - Math.min(2, String(score).length + String(maxScore).length - 5)}`} 
              fill="#333333" 
              fontWeight="bold">
              {score}/{maxScore}
            </text>
          </svg>
          <div className="absolute bottom-0 right-0 w-8 h-8 rounded-full shadow-md bg-white flex items-center justify-center">
            {percentage >= 75 ? (
              <IconWrapper icon={FiCheckCircle} className="text-green-500" size={20} />
            ) : percentage < 50 ? (
              <IconWrapper icon={FiAlertTriangle} className="text-red-500" size={20} />
            ) : null}
          </div>
        </div>
        
        <div className="flex-1">
          <div className="flex items-center mb-2">
            <span className="text-xl font-bold mr-2">{scoreLevel}</span>
            {previousScore && (
              <span className={`text-sm ${trendPercentage > 0 ? 'text-green-500' : trendPercentage < 0 ? 'text-red-500' : 'text-gray-500'}`}>
                {trendPercentage > 0 ? '▲' : trendPercentage < 0 ? '▼' : '•'} 
                {Math.abs(trendPercentage).toFixed(1)}%
              </span>
            )}
          </div>
          
          {details && (
            <p className="text-sm text-secondary/70">{details}</p>
          )}
        </div>
      </div>
      
      <div>
        <div className="grid grid-cols-5 gap-1 mb-2">
          <div className="h-1 bg-red-400 rounded-l"></div>
          <div className="h-1 bg-orange-400"></div>
          <div className="h-1 bg-yellow-400"></div>
          <div className="h-1 bg-green-400"></div>
          <div className="h-1 bg-green-500 rounded-r"></div>
        </div>
        <div className="flex justify-between text-xs text-secondary/50">
          <span>Critical</span>
          <span>Needs Work</span>
          <span>Satisfactory</span>
          <span>Good</span>
          <span>Excellent</span>
        </div>
      </div>
    </div>
  );
};

export default NCDScoreCard;